import React from 'react';
import { useTranslation } from 'react-i18next';
import { FormControl, InputLabel } from '@material-ui/core';
import { Card } from '../../../../components/shared/Card';
import { Input } from '../../../../components/shared/inputs';
import { ColorPicker } from '../../../../components/ColorPicker';
import FileUpload from '../../../../components/FileUpload';
import CustomLink from '../../../../components/CustomLink';
import { MAX_SCREEN_IMAGE_SIZE_BYTES } from '../../../../util/files';
import { ClassicHeaderSelect, VerticalHeaderSelect } from '../HeaderTypeSelect';
import { CLASSIC_HEADER_TYPE, VERTICAL_HEADER_TYPE } from '../enums';

interface StyleSectionProps {
  isClassicMenu: boolean;
  isVerticalMenu: boolean;
  primaryColor: string;
  onPrimaryColorChange: (hex: string) => void;
  secondaryColor: string;
  onSecondaryColorChange: (hex: string) => void;
  primaryTextColor: string;
  onPrimaryTextColorChange: (hex: string) => void;
  secondaryTextColor: string;
  onSecondaryTextColorChange: (hex: string) => void;
  dashColor: string;
  onDashColorChange: (hex: string) => void;
  classicHeaderType: CLASSIC_HEADER_TYPE;
  onClassicHeaderTypeChange: (value: string) => void;
  verticalHeaderType: VERTICAL_HEADER_TYPE;
  onVerticalHeaderTypeChange: (value: string) => void;
  headerTypeIsLogo: boolean;
  headerImagePreviewUrl: string | undefined;
  headerImageUrl: string | undefined;
  onHeaderImageSelected: (file: File, previewUrl: string) => void;
  onRemoveHeaderImage: () => void;
  footerImagePreviewUrl: string | undefined;
  footerImageUrl: string | undefined;
  onFooterImageSelected: (file: File, previewUrl: string) => void;
  onRemoveFooterImage: () => void;
  footerDate: string;
  onFooterDateChange: (value: string) => void;
  footerLocation: string;
  onFooterLocationChange: (value: string) => void;
  footerWebsite: string;
  onFooterWebsiteChange: (value: string) => void;
}

export const StyleSection: React.FC<StyleSectionProps> = ({
  isClassicMenu,
  isVerticalMenu,
  primaryColor,
  onPrimaryColorChange,
  secondaryColor,
  onSecondaryColorChange,
  primaryTextColor,
  onPrimaryTextColorChange,
  secondaryTextColor,
  onSecondaryTextColorChange,
  dashColor,
  onDashColorChange,
  classicHeaderType,
  onClassicHeaderTypeChange,
  verticalHeaderType,
  onVerticalHeaderTypeChange,
  headerTypeIsLogo,
  headerImagePreviewUrl,
  headerImageUrl,
  onHeaderImageSelected,
  onRemoveHeaderImage,
  footerImagePreviewUrl,
  footerImageUrl,
  onFooterImageSelected,
  onRemoveFooterImage,
  footerDate,
  onFooterDateChange,
  footerLocation,
  onFooterLocationChange,
  footerWebsite,
  onFooterWebsiteChange
}) => {
  const { t } = useTranslation();

  return (
    <Card className="border border-gray-200 rounded-lg p-6">
      <h2 className="text-lg font-semibold text-gray-800 mb-1">{t('PrintDialog.Style')}</h2>
      <p className="text-sm text-gray-500 mb-4">{t('PrintDialog.StyleSectionHelper')}</p>

      <div className="space-y-6">
        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">{t('PrintDialog.HeaderOptions')}</h3>
          <div className="w-1/2">
            <FormControl fullWidth variant="outlined">
              <InputLabel id="header-type">{t('PrintDialog.HeaderType')}</InputLabel>
              {isClassicMenu && (
                <ClassicHeaderSelect value={classicHeaderType} onChange={(e: any) => onClassicHeaderTypeChange(e.target.value)} />
              )}
              {isVerticalMenu && (
                <VerticalHeaderSelect value={verticalHeaderType} onChange={(e: any) => onVerticalHeaderTypeChange(e.target.value)} />
              )}
            </FormControl>
          </div>

          {headerTypeIsLogo && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-gray-500">{t('PrintDialog.HeaderImageSize')}</p>
              <FileUpload onFileSelected={onHeaderImageSelected} maxFileSize={MAX_SCREEN_IMAGE_SIZE_BYTES} id="header" />
              {headerImagePreviewUrl && <img src={headerImagePreviewUrl} alt="Header preview" className="max-w-xs" />}
              {(headerImageUrl || headerImagePreviewUrl) && (
                <p>
                  <CustomLink onClick={onRemoveHeaderImage}>{t('PrintDialog.RemoveHeaderImage')}</CustomLink>
                </p>
              )}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">{t('PrintDialog.Colors')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <ColorPicker
              color={primaryTextColor}
              label={t('PrintDialog.PrimaryTextColor')}
              onColorSelected={(c: any) => onPrimaryTextColorChange(c.hex)}
            />
            <ColorPicker
              color={secondaryTextColor}
              label={t('PrintDialog.SecondaryTextColor')}
              onColorSelected={(c: any) => onSecondaryTextColorChange(c.hex)}
            />
            <ColorPicker
              color={primaryColor}
              label={t('PrintDialog.PrimaryColor')}
              onColorSelected={(c: any) => onPrimaryColorChange(c.hex)}
            />
            <ColorPicker
              color={secondaryColor}
              label={t('PrintDialog.SecondaryColor')}
              onColorSelected={(c: any) => onSecondaryColorChange(c.hex)}
            />
            <ColorPicker color={dashColor} label={t('PrintDialog.DashColor')} onColorSelected={(c: any) => onDashColorChange(c.hex)} />
          </div>
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">{t('PrintDialog.FooterImage')}</h3>
          <p className="text-sm text-gray-500 mb-3">{t('PrintDialog.FooterImageSize')}</p>
          <FileUpload onFileSelected={onFooterImageSelected} maxFileSize={MAX_SCREEN_IMAGE_SIZE_BYTES} id="footer" />
          {footerImagePreviewUrl && <img src={footerImagePreviewUrl} alt="Footer preview" className="max-w-xs mt-3" />}
          {(footerImageUrl || footerImagePreviewUrl) && (
            <p className="mt-2">
              <CustomLink onClick={onRemoveFooterImage}>{t('PrintDialog.RemoveFooterImage')}</CustomLink>
            </p>
          )}
        </div>

        <div>
          <h3 className="text-base font-medium text-gray-700 mb-3">{t('PrintDialog.FooterText')}</h3>
          <div className="grid grid-cols-2 gap-4">
            <Input label={t('PrintDialog.Date')} value={footerDate} onChange={(v) => onFooterDateChange(String(v ?? ''))} />
            <Input label={t('PrintDialog.Location')} value={footerLocation} onChange={(v) => onFooterLocationChange(String(v ?? ''))} />
          </div>
          <div className="mt-4">
            <Input label={t('PrintDialog.Website')} value={footerWebsite} onChange={(v) => onFooterWebsiteChange(String(v ?? ''))} />
          </div>
        </div>
      </div>
    </Card>
  );
};
